import {sendEvent} from '@github-ui/hydro-analytics'
import {InlineAutocomplete} from '@github-ui/inline-autocomplete'
import type {SelectSuggestionsEvent, ShowSuggestionsEvent, Trigger} from '@github-ui/inline-autocomplete/types'
import type {DetailedHTMLProps, InputHTMLAttributes, ReactElement} from 'react'
import {useEffect, useState} from 'react'

import {useChatState} from '../../utils/CopilotChatContext'
import type {CategoryValue} from './categories'
import {type AutocompleteQuery, useAutocompleteSuggestions} from './useAutocompleteSuggestions'

const triggers: Trigger[] = [
  {triggerChar: '@', insertSpaceOnCommit: true, keepTriggerCharOnCommit: true, multiWord: true},
]

/** Returns a blank query corresponding to the selected category. */
function selectedCategoryQuery(category: CategoryValue): AutocompleteQuery {
  switch (category) {
    case 'repositories':
      return {category: 'repositories', filter: ''}
    case 'agents':
      return {category: 'agents', filter: ''}
    case 'repositories:discussions':
      return {category: 'repositories', filter: '', nextCategory: 'discussions'}
    case 'repositories:issues':
      return {category: 'repositories', filter: '', nextCategory: 'issues'}
    case 'repositories:pulls':
      return {category: 'repositories', filter: '', nextCategory: 'pulls'}
  }
}

export function UnifiedAutocomplete({
  children,
}: {
  children: ReactElement<DetailedHTMLProps<InputHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>>
}) {
  const {mode} = useChatState()

  const [mounted, setMounted] = useState(() => typeof document !== 'undefined')
  useEffect(() => setMounted(true), [])

  const [query, setQuery] = useState<AutocompleteQuery | null>(null)
  const suggestions = useAutocompleteSuggestions(query)

  if (!mounted) return <div>{children}</div>

  const onSelectSuggestion = ({suggestion}: SelectSuggestionsEvent) => {
    sendEvent('dotcom_chat.activate', {
      target: 'AUTOCOMPLETE_ITEM_SELECTED',
      mode,
      suggestion: typeof suggestion === 'string' ? suggestion : suggestion.value,
    })

    // Reopen the menu for multistep queries
    const isMultistepSuggestion = typeof suggestion === 'object' && suggestion.value === null
    if (!isMultistepSuggestion) return

    switch (query?.category) {
      case 'categories':
        setQuery(selectedCategoryQuery(suggestion.key as CategoryValue))
        break
      case 'repositories':
        if (query.nextCategory) setQuery({category: query.nextCategory, filter: '', repository: suggestion.key})
        break
    }
  }

  const onShowSuggestions = (event: ShowSuggestionsEvent) =>
    setQuery(q => (q ? {...q, filter: event.query} : {category: 'categories', filter: event.query}))

  const onHideSuggestions = () => setQuery(null)

  return (
    <InlineAutocomplete
      fullWidth
      suggestions={suggestions}
      triggers={triggers}
      tabInsertsSuggestions={false}
      onHideSuggestions={onHideSuggestions}
      onShowSuggestions={onShowSuggestions}
      onSelectSuggestion={onSelectSuggestion}
    >
      {children}
    </InlineAutocomplete>
  )
}

try{ UnifiedAutocomplete.displayName ||= 'UnifiedAutocomplete' } catch {}